import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import AgentDashboard from "@/pages/AgentDashboard";
import TeamPerformance from "@/pages/TeamPerformance";
import { pct, pctColor, MONTHS, MONTHS_F } from "@/lib/kpi";

const CUR_Y = 2026;

export default function DirectorDashboard() {
  const { logout } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingAgent, setViewingAgent] = useState(null);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("agent123");
  const [newAgence, setNewAgence] = useState("Orpi Mougel Immobilier");
  const [addErr, setAddErr] = useState("");

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editAgence, setEditAgence] = useState("");
  const [editPwd, setEditPwd] = useState("");
  const [editErr, setEditErr] = useState("");

  // Delete confirm
  const [deleting, setDeleting] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setAgents(data);
    } catch (e) {
      console.error("Failed to load users", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (viewingAgent) {
    return (
      <AgentDashboard
        targetUser={viewingAgent}
        onBack={() => {
          setViewingAgent(null);
          reload();
        }}
      />
    );
  }

  const curM = new Date().getMonth();
  const totCA = agents.reduce((s, a) => s + (Number(a.month?.ca) || 0), 0);
  const totM = agents.reduce((s, a) => s + (Number(a.month?.mandats) || 0), 0);
  const totC = agents.reduce((s, a) => s + (Number(a.month?.compromis) || 0), 0);

  const moveAgent = async (id, dir) => {
    const idx = agents.findIndex((a) => a.id === id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= agents.length) return;
    const reordered = [...agents];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setAgents(reordered);
    try {
      await api.post("/users/reorder", { ids: reordered.map((a) => a.id) });
    } catch (e) {
      alert(formatApiError(e));
      reload();
    }
  };

  const submitAdd = async () => {
    setAddErr("");
    if (!newName.trim() || !newEmail.trim() || !newPwd.trim()) {
      setAddErr("Tous les champs sont requis");
      return;
    }
    try {
      await api.post("/users", {
        name: newName.trim(),
        email: newEmail.trim().toLowerCase(),
        password: newPwd,
        agence: newAgence,
      });
      setShowAdd(false);
      setNewName("");
      setNewEmail("");
      setNewPwd("agent123");
      setNewAgence("Orpi Mougel Immobilier");
      reload();
    } catch (e) {
      setAddErr(formatApiError(e));
    }
  };

  const openEdit = (a) => {
    setEditing(a);
    setEditName(a.name);
    setEditEmail(a.email);
    setEditAgence(a.agence);
    setEditPwd("");
    setEditErr("");
  };

  const submitEdit = async () => {
    setEditErr("");
    try {
      const payload = {
        name: editName.trim(),
        email: editEmail.trim().toLowerCase(),
        agence: editAgence,
      };
      if (editPwd.trim()) payload.password = editPwd;
      await api.patch(`/users/${editing.id}`, payload);
      setEditing(null);
      reload();
    } catch (e) {
      setEditErr(formatApiError(e));
    }
  };

  const submitDelete = async () => {
    try {
      await api.delete(`/users/${deleting.id}`);
      setDeleting(null);
      reload();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="director-dashboard">
      <div className="hdr">
        <div>
          <div className="hdr-label">
            Orpi Mougel Immobilier · Orpi Mougel Immobilier Pro
          </div>
          <div className="hdr-title">Tableau de bord KPIs</div>
          <div className="hdr-sub">
            Vue Équipe — {MONTHS_F[curM]} {CUR_Y}
          </div>
        </div>
        <div className="hdr-actions">
          <button
            className="btn btn-red btn-sm"
            onClick={() => setShowAdd(true)}
            data-testid="add-agent-btn"
          >
            + Collaborateur
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={logout}
            data-testid="logout-btn"
          >
            Quitter
          </button>
        </div>
      </div>

      <div className="main">
        {/* Performance équipe (multi-période) */}
        <TeamPerformance />

        {/* Synthèse mois courant */}
        <div className="synth-grid">
          <div className="card synth-card">
            <div className="synth-label">CA Total Équipe</div>
            <div className="synth-val" style={{ color: "#c0392b" }} data-testid="synth-ca">
              {totCA.toLocaleString("fr-FR")} €
            </div>
          </div>
          <div className="card synth-card">
            <div className="synth-label">Total Mandats</div>
            <div className="synth-val" style={{ color: "#1a1a1a" }} data-testid="synth-mandats">
              {totM}
            </div>
          </div>
          <div className="card synth-card">
            <div className="synth-label">Total Compromis</div>
            <div className="synth-val" style={{ color: "#2c3e50" }} data-testid="synth-compromis">
              {totC}
            </div>
          </div>
          <div className="card synth-card">
            <div className="synth-label">Conseillers</div>
            <div className="synth-val" style={{ color: "#1a1a1a" }} data-testid="synth-count">
              {agents.length}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
            <div className="sec-title" style={{ marginBottom: 0 }}>
              Comparatif par conseiller — {MONTHS[curM].toUpperCase()} {CUR_Y}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 48 }}></th>
                  <th>Conseiller</th>
                  <th>Agence</th>
                  <th>CA Réalisé</th>
                  <th>% Obj. CA</th>
                  <th>Mandats</th>
                  <th>Compromis</th>
                  <th></th>
                </tr>
              </thead>
              <tbody data-testid="agents-table">
                {loading ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{ padding: 40, textAlign: "center", color: "#aaa" }}
                    >
                      Chargement…
                    </td>
                  </tr>
                ) : agents.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        padding: 48,
                        textAlign: "center",
                        color: "#aaa",
                        fontStyle: "italic",
                      }}
                    >
                      Aucun collaborateur. Cliquez sur "+ Collaborateur".
                    </td>
                  </tr>
                ) : (
                  agents.map((a, i) => {
                    const ca = Number(a.month?.ca) || 0;
                    const p = pct(ca, a.objs?.ca);
                    const pc = pctColor(p);
                    return (
                      <tr
                        key={a.id}
                        onClick={() => setViewingAgent(a)}
                        data-testid={`agent-row-${a.id}`}
                      >
                        <td
                          onClick={(e) => e.stopPropagation()}
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          <div
                            style={{ display: "flex", flexDirection: "column", gap: 2 }}
                          >
                            <button
                              className="btn btn-outline btn-xs"
                              onClick={() => moveAgent(a.id, -1)}
                              disabled={i === 0}
                              aria-label="Monter"
                            >
                              ↑
                            </button>
                            <button
                              className="btn btn-outline btn-xs"
                              onClick={() => moveAgent(a.id, 1)}
                              disabled={i === agents.length - 1}
                              aria-label="Descendre"
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar name={a.name} size={30} />
                            <span style={{ fontWeight: 700 }}>{a.name}</span>
                          </div>
                        </td>
                        <td style={{ color: "#aaa", fontSize: 11, whiteSpace: "nowrap" }}>
                          {a.agence}
                        </td>
                        <td style={{ color: "#c0392b", fontWeight: 800 }}>
                          {ca.toLocaleString("fr-FR")} €
                        </td>
                        <td>
                          <div className="prog-wrap">
                            <div className="prog-bar">
                              <div
                                className="prog-fill"
                                style={{
                                  background: pc,
                                  width: `${Math.min(100, p)}%`,
                                }}
                              />
                            </div>
                            <span
                              style={{ color: pc, fontSize: 12, fontWeight: 700 }}
                            >
                              {p}%
                            </span>
                          </div>
                        </td>
                        <td
                          style={{ color: "#c0392b", fontWeight: 700, fontSize: 15 }}
                        >
                          {Number(a.month?.mandats) || 0}
                        </td>
                        <td
                          style={{ color: "#2c3e50", fontWeight: 700, fontSize: 15 }}
                        >
                          {Number(a.month?.compromis) || 0}
                        </td>
                        <td
                          onClick={(e) => e.stopPropagation()}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => openEdit(a)}
                              data-testid={`edit-agent-${a.id}`}
                              aria-label="Modifier"
                            >
                              Modifier
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => setDeleting(a)}
                              data-testid={`delete-agent-${a.id}`}
                              aria-label="Supprimer"
                            >
                              Suppr.
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div
          className="overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAdd(false);
          }}
          data-testid="add-agent-modal"
        >
          <div className="modal-box">
            <div className="sec-title">Ajouter un collaborateur</div>
            {addErr && <div className="login-err">{addErr}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="form-label">Nom complet</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Prénom NOM"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="add-name-input"
                />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="pnom@orpi.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="add-email-input"
                />
              </div>
              <div>
                <label className="form-label">Mot de passe initial</label>
                <input
                  className="form-input"
                  type="text"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  data-testid="add-password-input"
                />
              </div>
              <div>
                <label className="form-label">Agence</label>
                <select
                  className="form-select"
                  value={newAgence}
                  onChange={(e) => setNewAgence(e.target.value)}
                  data-testid="add-agence-select"
                >
                  <option>Orpi Mougel Immobilier</option>
                  <option>Orpi Mougel Immobilier Pro</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAdd(false)}>
                Annuler
              </button>
              <button
                className="btn btn-dark"
                onClick={submitAdd}
                data-testid="submit-add-btn"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div
          className="overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
          data-testid="edit-agent-modal"
        >
          <div className="modal-box">
            <div className="sec-title">Modifier le collaborateur</div>
            {editErr && <div className="login-err">{editErr}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="form-label">Nom complet</label>
                <input
                  className="form-input"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  data-testid="edit-name-input"
                />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  data-testid="edit-email-input"
                />
              </div>
              <div>
                <label className="form-label">Nouveau mot de passe (optionnel)</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Laisser vide pour conserver"
                  value={editPwd}
                  onChange={(e) => setEditPwd(e.target.value)}
                  data-testid="edit-password-input"
                />
              </div>
              <div>
                <label className="form-label">Agence</label>
                <select
                  className="form-select"
                  value={editAgence}
                  onChange={(e) => setEditAgence(e.target.value)}
                  data-testid="edit-agence-select"
                >
                  <option>Orpi Mougel Immobilier</option>
                  <option>Orpi Mougel Immobilier Pro</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setEditing(null)}>
                Annuler
              </button>
              <button
                className="btn btn-dark"
                onClick={submitEdit}
                data-testid="submit-edit-btn"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleting && (
        <div className="overlay" data-testid="delete-confirm-modal">
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-title">Supprimer ce collaborateur ?</div>
            <div
              style={{
                fontSize: 13,
                color: "#888",
                margin: "10px 0 24px",
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "#1a1a1a" }}>{deleting.name}</strong> sera
              retiré de la liste ainsi que ses données.
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setDeleting(null)}>
                Annuler
              </button>
              <button
                className="btn btn-red"
                onClick={submitDelete}
                data-testid="confirm-delete-btn"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
